import type { CSSProperties, ReactNode } from 'react';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import { IconoAlerta } from './Iconos';

/** Tarjeta de KPI, con la franja de acento superior del diseño original. */
export function TarjetaKpi({
  etiqueta,
  valor,
  acento,
  colorValor,
  nota,
  insignia,
  onClick,
  style,
}: {
  etiqueta: string;
  valor: ReactNode;
  acento?: string;
  colorValor?: string;
  nota?: ReactNode;
  insignia?: { texto: string; color: string; fondo: string };
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <div
      className="kpi-card"
      onClick={onClick}
      style={
        {
          '--acento': acento ?? 'transparent',
          cursor: onClick ? 'pointer' : undefined,
          ...style,
        } as CSSProperties
      }
    >
      <div className="kpi-label">{etiqueta}</div>
      <div className="kpi-value" style={colorValor ? { color: colorValor } : undefined}>
        {valor}
      </div>
      {insignia && (
        <span className="kpi-badge" style={{ background: insignia.fondo, color: insignia.color }}>
          {insignia.texto}
        </span>
      )}
      {nota && <div className="kpi-nota">{nota}</div>}
    </div>
  );
}

export function Panel({
  titulo,
  subtitulo,
  acciones,
  children,
  style,
}: {
  titulo?: string;
  subtitulo?: string;
  acciones?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section className="panel" style={style}>
      {(titulo || acciones) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, marginBottom: subtitulo ? 4 : 16 }}>
          {titulo && <h3 className="panel-titulo">{titulo}</h3>}
          {acciones}
        </div>
      )}
      {subtitulo && <p className="panel-sub" style={{ marginBottom: 16 }}>{subtitulo}</p>}
      {children}
    </section>
  );
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return <div className="cargando">{texto}</div>;
}

/**
 * Estado vacío o de error.
 *
 * Un vacío siempre explica por qué está vacío y qué hacer. «Sin resultados» a
 * secas deja al usuario sin saber si filtró de más o si el sistema falló.
 */
export function Vacio({ titulo, detalle, accion }: { titulo: string; detalle?: string; accion?: ReactNode }) {
  return (
    <div className="vacio">
      <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{titulo}</div>
      {detalle && <div style={{ fontSize: 12, maxWidth: 460, margin: '0 auto', lineHeight: 1.55 }}>{detalle}</div>}
      {accion && <div style={{ marginTop: 16 }}>{accion}</div>}
    </div>
  );
}

export function ErrorCarga({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  const mensaje = error instanceof Error ? error.message : 'Ocurrió un error inesperado.';

  return (
    <div className="vacio">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--accent-red)', fontWeight: 600, marginBottom: 8 }}>
        <IconoAlerta />
        No se pudieron cargar los datos
      </div>
      <div style={{ fontSize: 12, maxWidth: 460, margin: '0 auto', lineHeight: 1.55 }}>{mensaje}</div>
      {reintentar && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={reintentar}>
          Reintentar
        </button>
      )}
    </div>
  );
}

export function Insignia({ estado, children }: { estado: string; children?: ReactNode }) {
  return <span className={`badge badge-${estado}`}>{children ?? ETIQUETAS[estado] ?? estado}</span>;
}

const ETIQUETAS: Record<string, string> = {
  OPORTUNIDAD: 'Oportunidad',
  EN_PREPARACION: 'En preparación',
  OFERTA_ENVIADA: 'Oferta enviada',
  ADJUDICADA: 'Adjudicada',
  PERDIDA: 'Perdida',
  DESIERTA: 'Desierta',
  PLANIFICADO: 'Planificado',
  EN_EJECUCION: 'En ejecución',
  EN_PAUSA: 'En pausa',
  CERRADO: 'Cerrado',
  CANCELADO: 'Cancelado',
};

/** Barra de consumo de presupuesto, con los tres estados del diseño original. */
export function BarraPresupuesto({ porcentaje }: { porcentaje: number }) {
  const clase = porcentaje >= 100 ? 'excedido' : porcentaje >= 80 ? 'aviso' : '';

  return (
    <div className="budget-track">
      <div className={`budget-fill ${clase}`} style={{ width: `${Math.min(100, Math.max(0, porcentaje))}%` }} />
    </div>
  );
}

export function BarraAvance({ porcentaje, enRiesgo }: { porcentaje: number; enRiesgo?: boolean }) {
  return (
    <div className="progress-track">
      <div
        className={`progress-fill${enRiesgo ? ' riesgo' : ''}`}
        style={{ width: `${Math.min(100, Math.max(0, porcentaje))}%` }}
      />
    </div>
  );
}

/** Monto en pesos, en la tipografía monoespaciada del diseño. */
export function Monto({ valor, color, className }: { valor: number | null | undefined; color?: string; className?: string }) {
  return (
    <span className={`td-mono ${className ?? ''}`} style={color ? { color } : undefined}>
      {formatearCLP(valor)}
    </span>
  );
}

export function Porcentaje({ valor, decimales = 0 }: { valor: number | null | undefined; decimales?: number }) {
  return <>{formatearPorcentajeSimple(valor, decimales)}</>;
}
